import { Link, TacoRating } from '@appsumo/dorado-react';
import Paragraphs from '@render-props/paragraphs';
import clsx from 'clsx';
import Image from 'next/image';
import { useMemo } from 'react';
import {
  CommentV2 as CommentV2Type,
  DISCUSSION_STATUS_REJECTED,
  DISCUSSION_STATUS_UNMODERATED,
  DiscussionStatus as DiscussionStatusType,
  ReviewV2,
} from '~/lib/discussions/types';
import { ActionsV2, ButtonsV2, UserMetaV2 } from '.';
import DiscussionStatus from './DiscussionStatus';

export function CommentV2({
  href,
  comment,
  mutateComment,
  moderator = false,
  border = false,
  className = '',
  isReview = false,
}: {
  href: string;
  comment: CommentV2Type;
  mutateComment: () => void;
  moderator?: boolean;
  border?: boolean;
  className?: string;
  isReview?: boolean;
}) {
  const { user } = comment;
  const status: null | 'pending' | 'approved' | 'denied' = useMemo(() => {
    if (!moderator) {
      return null;
    }

    const status = comment.status?.toLowerCase();

    if (status === DISCUSSION_STATUS_UNMODERATED) {
      return 'pending';
    }

    if (status === DISCUSSION_STATUS_REJECTED) {
      return 'denied';
    }

    return null;
  }, [moderator, comment.status]);

  const commentHasStatus = useMemo(() => {
    return (
      (moderator && status && ['pending', 'denied'].includes(status)) ||
      comment.resolved === false
    );
  }, [moderator, status, comment.resolved]);

  return (
    <div
      id={`${comment.id}`}
      className={clsx(
        'relative mt-8 flex flex-row gap-x-4 p-12 max-sm:mt-4 max-sm:p-4',
        border ? 'rounded border border-gray-300' : '',
        moderator && status === 'pending'
          ? 'rounded border border-sunset shadow-md shadow-sunset'
          : '',
        moderator && status === 'denied'
          ? 'rounded border border-ready-red shadow-md shadow-ready-red'
          : '',
        moderator && comment.resolved === false
          ? 'rounded border border-entrepurple shadow-md shadow-entrepurple'
          : '',
        commentHasStatus && 'max-sm:mt-10',
        className,
      )}
    >
      {moderator && (status !== null || comment.resolved === false) && (
        <div className="absolute left-0 top-0 flex -translate-y-full gap-x-2">
          <DiscussionStatus
            isApproved={comment.approved}
            isResolved={comment.resolved}
            discussionStatus={
              comment.status?.toLowerCase() as DiscussionStatusType | undefined
            }
          />
        </div>
      )}
      <div className="min-w-[70px] max-sm:min-w-[40px]">
        <Link href={`/profile/${user.username}/`} target="_blank">
          <Image
            src={user.avatar}
            alt={user.username}
            width={70}
            height={70}
            unoptimized
            className="h-[70px] w-[70px] rounded-full max-sm:h-[40px] max-sm:w-[40px]"
          />
        </Link>
      </div>
      <div className="min-w-0 grow">
        <UserMetaV2
          user={comment.user}
          posted={comment.created}
          purchased={comment.purchased}
        />
        <div className="break-words">
          {isReview && !!(comment as ReviewV2).rating && (
            <TacoRating
              averageRating={(comment as ReviewV2).rating}
              href={href}
              size="md"
              className="mt-4"
            />
          )}
          <h5 className="my-4 font-bold">{comment.title}</h5>
          <Paragraphs text={comment.comment}>
            {({ key, text }: { key: number; text: string }) => (
              <p key={key} className={'my-4'}>
                {text}
              </p>
            )}
          </Paragraphs>
        </div>
        <ButtonsV2
          comment={comment}
          mutateComment={mutateComment}
          isReview={isReview}
        />
        <ActionsV2
          href={href}
          comment={comment}
          moderator={moderator}
          mutateComment={mutateComment}
        />
      </div>
    </div>
  );
}
